import {
    DATETIME_CONTRAST_BLACK,
    DATETIME_CONTRAST_BLACK_RGB,
    DATETIME_CONTRAST_COLUMN_STYLE_ATTRIBUTE,
    DATETIME_CONTRAST_COLUMN_STYLE_CSS,
    DATETIME_CONTRAST_COLUMN_STYLE_SELECTOR,
    DATETIME_CONTRAST_PICKER_STYLE_ATTRIBUTE,
    DATETIME_CONTRAST_PICKER_STYLE_CSS,
    DATETIME_CONTRAST_PICKER_STYLE_SELECTOR,
    DATETIME_CONTRAST_YELLOW,
    DATETIME_CONTRAST_YELLOW_RGB,
    DATETIME_DATE_ONLY_REGEX,
    DATETIME_DATE_TIME_REGEX,
    DATETIME_CSS_VAR_BACKGROUND,
    DATETIME_CSS_VAR_BACKGROUND_RGB,
    DATETIME_CSS_VAR_COLOR_STEP_150,
    DATETIME_END_OF_DAY,
    DATETIME_PRESENTATION_DATE,
    DATETIME_PRESENTATION_DATE_TIME,
    DATETIME_START_OF_DAY,
    DATETIME_WHEEL_PICKER_STYLE_ATTRIBUTE,
    DATETIME_WHEEL_PICKER_DARK_STYLE_CSS,
    DATETIME_WHEEL_PICKER_LIGHT_STYLE_CSS,
    DATETIME_WHEEL_PICKER_STYLE_SELECTOR
} from './datetime-picker.constants';
import type Env from '@ridango/core/env';

type DatetimeWithReady = HTMLElement & { componentOnReady?: () => Promise<unknown> };
const WHEEL_COLUMN_SELECTOR = 'ion-picker-column-internal';
const WHEEL_ITEM_SELECTOR = '.picker-item';
type DatetimePickerEnv = Pick<Env, 'ENABLE_WHEEL_DATE_TIME_PICKER'>;

export type DatetimeWheelAccessibilityLabels = {
    date: string;
    hours: string;
    minutes: string;
};

export type DatetimePresentationState = {
    hideTime: boolean;
    isTimeNeeded: boolean;
    presentationMode: string;
};

export class DatetimePickerUtils {
    static resolvePresentationState(
        mode: string,
        env?: DatetimePickerEnv,
        forceNativeDatePresentation = false
    ): DatetimePresentationState {
        if (forceNativeDatePresentation && mode === DATETIME_PRESENTATION_DATE) {
            return {
                presentationMode: DATETIME_PRESENTATION_DATE,
                isTimeNeeded: false,
                hideTime: false
            };
        }

        const wheelEnabled = env?.ENABLE_WHEEL_DATE_TIME_PICKER === true;
        const isDateMode = mode === DATETIME_PRESENTATION_DATE || mode === DATETIME_PRESENTATION_DATE_TIME;

        return {
            presentationMode: wheelEnabled && isDateMode ? DATETIME_PRESENTATION_DATE_TIME : mode,
            isTimeNeeded: mode === DATETIME_PRESENTATION_DATE_TIME,
            hideTime: mode === DATETIME_PRESENTATION_DATE
        };
    }

    static normalizeIonChangeEvent(event: Event): { isUserEvent: boolean; normalizedValue: string | null } {
        const datetimeEvent = event as CustomEvent<{ value: string | string[] | null }>;
        const nextValue = Array.isArray(datetimeEvent.detail.value)
            ? datetimeEvent.detail.value[0]
            : datetimeEvent.detail.value;

        return {
            normalizedValue: nextValue ?? null,
            isUserEvent: event.isTrusted
        };
    }

    /**
     * Normalizes incoming datetime bounds/value to an Ionic-safe format (`YYYY-MM-DDTHH:mm`).
     * Prevents `ion-datetime` crashes on mixed/offset formats and expands date-only values
     * to start/end of day when date-only mode is forced into `date-time` presentation.
     */
    static sanitizeInputValue(
        value: string | null | undefined,
        forceDateTime: boolean,
        dateOnlyMode: 'start' | 'end' = 'start'
    ): string | null | undefined {
        if (value == null) {
            return value;
        }

        const normalized = value.trim();
        if (!normalized) {
            return undefined;
        }

        const dateOnlyMatch = normalized.match(DATETIME_DATE_ONLY_REGEX);
        const dateTimeMatch = normalized.match(DATETIME_DATE_TIME_REGEX);

        if (dateOnlyMatch) {
            if (!forceDateTime) {
                return dateOnlyMatch[1];
            }

            return dateOnlyMode === 'end'
                ? `${dateOnlyMatch[1]}T${DATETIME_END_OF_DAY}`
                : `${dateOnlyMatch[1]}T${DATETIME_START_OF_DAY}`;
        }

        if (dateTimeMatch) {
            const [, datePart, hourPart, minutePart] = dateTimeMatch;
            return `${datePart}T${hourPart}:${minutePart}`;
        }

        return undefined;
    }

    static areBoundsValid(min?: string, max?: string): boolean {
        if (!min || !max) {
            return true;
        }

        return min <= max;
    }

    static applyShadowStyle(
        datetimeElement: DatetimeWithReady,
        hideTime: boolean,
        styleSelector: string,
        styleAttribute: string,
        styleCss: string
    ): void {
        this.runWhenShadowReady(datetimeElement, (shadowRoot) => {
            const existingStyle = shadowRoot.querySelector(styleSelector);
            if (!hideTime) {
                existingStyle?.remove();
                return;
            }

            if (existingStyle) {
                return;
            }

            const style = document.createElement('style');
            style.setAttribute(styleAttribute, '');
            style.textContent = styleCss;
            shadowRoot.appendChild(style);
        });
    }

    static syncContrastWheelStyles(datetimeElement?: HTMLElement | null): void {
        if (!datetimeElement) {
            return;
        }

        const pickerInternals = datetimeElement.shadowRoot?.querySelectorAll('ion-picker-internal');
        const hasHighContrastContainer = !!datetimeElement.closest('.high-contrast');
        const hasHighContrastModal = !!document.querySelector('ion-modal.high-contrast');
        const isDarkContext =
            document.documentElement.classList.contains('dark') || hasHighContrastContainer || hasHighContrastModal;

        if (!isDarkContext) {
            datetimeElement.classList.remove('contrast-wheel');
            datetimeElement.style.removeProperty(DATETIME_CSS_VAR_COLOR_STEP_150);
            datetimeElement.style.removeProperty(DATETIME_CSS_VAR_BACKGROUND);
            datetimeElement.style.removeProperty(DATETIME_CSS_VAR_BACKGROUND_RGB);
            datetimeElement.shadowRoot?.querySelectorAll(WHEEL_COLUMN_SELECTOR).forEach((column) => {
                const columnEl = column as HTMLElement;
                columnEl.style.removeProperty('color');
                columnEl.style.removeProperty('--ion-color-base');
                columnEl.style.removeProperty('--ion-color-base-rgb');
                columnEl.shadowRoot?.querySelector(DATETIME_CONTRAST_COLUMN_STYLE_SELECTOR)?.remove();
            });
            pickerInternals?.forEach((picker) => {
                const pickerEl = picker as HTMLElement;
                pickerEl.shadowRoot?.querySelector(DATETIME_CONTRAST_PICKER_STYLE_SELECTOR)?.remove();
                pickerEl.style.removeProperty(DATETIME_CSS_VAR_COLOR_STEP_150);
                pickerEl.style.removeProperty(DATETIME_CSS_VAR_BACKGROUND);
                pickerEl.style.removeProperty(DATETIME_CSS_VAR_BACKGROUND_RGB);
            });
            return;
        }

        datetimeElement.classList.add('contrast-wheel');
        datetimeElement.style.setProperty(DATETIME_CSS_VAR_COLOR_STEP_150, DATETIME_CONTRAST_BLACK);
        datetimeElement.style.setProperty(DATETIME_CSS_VAR_BACKGROUND, DATETIME_CONTRAST_BLACK);
        datetimeElement.style.setProperty(DATETIME_CSS_VAR_BACKGROUND_RGB, DATETIME_CONTRAST_BLACK_RGB);

        pickerInternals?.forEach((picker) => {
            const pickerEl = picker as HTMLElement;
            pickerEl.style.setProperty(DATETIME_CSS_VAR_COLOR_STEP_150, DATETIME_CONTRAST_BLACK);
            pickerEl.style.setProperty(DATETIME_CSS_VAR_BACKGROUND, DATETIME_CONTRAST_BLACK);
            pickerEl.style.setProperty(DATETIME_CSS_VAR_BACKGROUND_RGB, DATETIME_CONTRAST_BLACK_RGB);

            if (!pickerEl.shadowRoot || pickerEl.shadowRoot.querySelector(DATETIME_CONTRAST_PICKER_STYLE_SELECTOR)) {
                return;
            }

            const pickerStyle = document.createElement('style');
            pickerStyle.setAttribute(DATETIME_CONTRAST_PICKER_STYLE_ATTRIBUTE, '');
            pickerStyle.textContent = DATETIME_CONTRAST_PICKER_STYLE_CSS;
            pickerEl.shadowRoot.prepend(pickerStyle);
        });

        datetimeElement.shadowRoot?.querySelectorAll(WHEEL_COLUMN_SELECTOR).forEach((column) => {
            const columnEl = column as HTMLElement;
            columnEl.style.color = 'rgba(255, 255, 0, 0.62)';
            columnEl.style.setProperty('--ion-color-base', DATETIME_CONTRAST_YELLOW);
            columnEl.style.setProperty('--ion-color-base-rgb', DATETIME_CONTRAST_YELLOW_RGB);

            const columnShadowRoot = columnEl.shadowRoot;
            if (!columnShadowRoot || columnShadowRoot.querySelector(DATETIME_CONTRAST_COLUMN_STYLE_SELECTOR)) {
                return;
            }

            const style = document.createElement('style');
            style.setAttribute(DATETIME_CONTRAST_COLUMN_STYLE_ATTRIBUTE, '');
            style.textContent = DATETIME_CONTRAST_COLUMN_STYLE_CSS;
            columnShadowRoot.prepend(style);
        });
    }

    static syncWheelPickerStyles(datetimeElement?: HTMLElement | null): void {
        if (!datetimeElement) {
            return;
        }

        const wheelPickerStyleCss = this.isDarkWheelContext(datetimeElement)
            ? DATETIME_WHEEL_PICKER_DARK_STYLE_CSS
            : DATETIME_WHEEL_PICKER_LIGHT_STYLE_CSS;

        datetimeElement.shadowRoot?.querySelectorAll('ion-picker-internal').forEach((picker) => {
            const pickerEl = picker as HTMLElement;
            const pickerShadowRoot = pickerEl.shadowRoot;
            if (!pickerShadowRoot) {
                return;
            }

            const existingStyle = pickerShadowRoot.querySelector<HTMLStyleElement>(
                DATETIME_WHEEL_PICKER_STYLE_SELECTOR
            );
            if (existingStyle) {
                existingStyle.textContent = wheelPickerStyleCss;
                return;
            }

            const style = document.createElement('style');
            style.setAttribute(DATETIME_WHEEL_PICKER_STYLE_ATTRIBUTE, '');
            style.textContent = wheelPickerStyleCss;
            pickerShadowRoot.prepend(style);
        });
    }

    static getWheelColumns(shadowRoot?: ShadowRoot | null): HTMLElement[] {
        if (!shadowRoot) {
            return [];
        }

        return Array.from(shadowRoot.querySelectorAll(WHEEL_COLUMN_SELECTOR)).filter(
            (column) => !(column as HTMLElement).hasAttribute('disabled')
        ) as HTMLElement[];
    }

    /**
     *  VoiceOver reads the wheel buttons directly, so each nested picker item needs an
     *  explicit contextual aria-label.
     *
     * @param shadowRoot
     * @param labels
     */
    static syncWheelAccessibility(
        shadowRoot: ShadowRoot | null | undefined,
        labels: DatetimeWheelAccessibilityLabels
    ): void {
        const columns = this.getWheelColumns(shadowRoot);
        let unnamedTimeColumnIndex = 0;

        columns.forEach((column) => {
            const columnLabel = this.resolveWheelColumnLabel(column, labels, unnamedTimeColumnIndex);
            if (!columnLabel) {
                return;
            }

            if (!column.classList.contains('date-column')) {
                unnamedTimeColumnIndex += 1;
            }

            column.setAttribute('aria-label', columnLabel);

            const activeItem = column.shadowRoot?.querySelector<HTMLElement>(`${WHEEL_ITEM_SELECTOR}-active`);
            const activeText = activeItem?.textContent?.trim();
            if (activeText) {
                column.setAttribute('aria-valuetext', `${columnLabel}, ${activeText}`);
            }

            this.getWheelItems(column).forEach((item) => {
                const text = item.textContent?.trim();
                if (!text) {
                    return;
                }

                item.setAttribute('aria-label', `${columnLabel}, ${text}`);
            });
        });
    }

    static runWhenShadowReady(datetimeElement: DatetimeWithReady, callback: (shadowRoot: ShadowRoot) => void): void {
        const run = () => {
            const shadowRoot = datetimeElement.shadowRoot;
            if (!shadowRoot) {
                return;
            }

            callback(shadowRoot);
        };

        if (datetimeElement.shadowRoot) {
            run();
            return;
        }

        if (datetimeElement.componentOnReady) {
            // `ion-datetime` attaches its Shadow DOM asynchronously, so callers must wait
            // before touching internals.
            void datetimeElement.componentOnReady().then(() => run());
            return;
        }

        // Test doubles and older runtimes may not expose `componentOnReady`, so fall back
        // to the next animation frame.
        requestAnimationFrame(() => run());
    }

    private static getWheelItems(column: HTMLElement): HTMLElement[] {
        return Array.from(column.shadowRoot?.querySelectorAll<HTMLElement>(WHEEL_ITEM_SELECTOR) ?? []);
    }

    private static isDarkWheelContext(datetimeElement: HTMLElement): boolean {
        return (
            document.documentElement.classList.contains('dark') ||
            !!datetimeElement.closest('.dark') ||
            !!datetimeElement.closest('.high-contrast') ||
            !!document.querySelector('ion-modal.dark') ||
            !!document.querySelector('ion-modal.high-contrast')
        );
    }

    private static resolveWheelColumnLabel(
        column: HTMLElement,
        labels: DatetimeWheelAccessibilityLabels,
        unnamedTimeColumnIndex: number
    ): string | null {
        if (column.classList.contains('date-column')) {
            return labels.date;
        }

        if (column.classList.contains('hour-column') || unnamedTimeColumnIndex === 0) {
            return labels.hours;
        }

        if (column.classList.contains('minute-column') || unnamedTimeColumnIndex === 1) {
            return labels.minutes;
        }

        return null;
    }
}
